import React, {
    useState, useContext, useEffect, useCallback
} from 'react';
import _ from 'lodash';
import { WizardPage, WizardPageTemplate, wizardProps } from 'gw-portals-wizard-react';
import { withRouter } from 'react-router-dom';
import { useValidation } from 'gw-portals-validation-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { TranslatorContext } from '@jutro/locale';
import { FNOLLossLocation, fnolCommonMessages } from 'gw-capability-fnol-common-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import vehicleUtils from '../../Vehicles/vehicleUtils';
import LossCauseUtil from './LossCauseUtil';
import metadata from './CollisionDetailsPage.metadata.json5';
import styles from './CollisionDetailsPage.module.scss';
import './CollisionDetailsPage.messages';
import messages from '../../../FNOLCA.messages';

function FNOLCACollisionDetailsPage(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        history: {
            location: { state = {} }
        }
    } = props;
    const translator = useContext(TranslatorContext);
    const [collisionTypeCodes, updateCollisionTypeCodes] = useState([]);
    const [showPropertyIncident, updateShowPropertyIncident] = useState(false);
    const lossPartyTypeList = vehicleUtils.getTypeList();
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation
    } = useValidation('FNOLCACollisionPage');

    useEffect(() => {
        const detailsCollisionTypeCodes = _.filter(
            claimVM.lossCause.aspects.availableValues,
            (typeCode) => {
                const nonCollisionAutoCodes = [
                    'theftentire',
                    'glassbreakage',
                    'theftparts',
                    'otherobjcoll'
                ];
                return nonCollisionAutoCodes.indexOf(typeCode.code) === -1;
            }
        ).map((collisionObj) => {
            return {
                code: collisionObj.code,
                name: translator({
                    id: collisionObj.name,
                    defaultMessage: collisionObj.name
                })
            };
        });
        const propertyTypeCheck = _.get(
            claimVM,
            'lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value'
        );
        if (!_.isEmpty(propertyTypeCheck)) {
            updateShowPropertyIncident(true);
        }
        updateCollisionTypeCodes(detailsCollisionTypeCodes);
        _.set(claimVM.value, 'propertyDamage', false);
        updateWizardData(claimVM);
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onHandlePropertyDamageChange = useCallback(
        (value, path) => {
            updateShowPropertyIncident(value);
            if (value === false) {
                _.set(
                    claimVM,
                    'lobs.commercialAuto.fixedPropertyIncident.propertyDescription.value',
                    undefined
                );
                _.set(
                    claimVM,
                    'lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value',
                    undefined
                );
            }
            _.set(claimVM.value, path, value);
            updateWizardData(claimVM);
        },
        [claimVM, updateWizardData]
    );

    const onHandleCollisionTypeChange = (value, path) => {
        _.set(claimVM, path, value);
        updateWizardData(claimVM);
    };

    const isClaimStatus = useCallback(() => {
        const { claimStatus } = state;
        return !_.isEmpty(claimStatus);
    }, [state]);

    const showPropertyDamageSection = () => {
        if (!_.isUndefined(claimVM) && !_.isUndefined(claimVM.lossCause) && !_.isUndefined(claimVM.lossCause.value) ) {
            const lossCauseCode = claimVM.lossCause.value.code;
            const collisionAndOtherCodes = LossCauseUtil.getCollisionCodes().concat(LossCauseUtil.getOtherCodes());
            const result = _.includes(collisionAndOtherCodes, lossCauseCode);
            return result;
        }
        return false;
    };

    useEffect(() => {
        registerInitialComponentValidation(isClaimStatus);
    }, [registerInitialComponentValidation, isClaimStatus]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left',
            showOptional: false
        },
        typeOfCollisionDropDown: {
            availableValues: collisionTypeCodes
        },
        propertyDamageContainer: {
            visible: showPropertyDamageSection()
        },
        wasAnyPropertyDamaged: {
            value: showPropertyIncident
        },
        fixedPropertyIncidentTypeExt: {
            visible: showPropertyIncident
        },
        describePropertyDamage: {
            value: _.get(
                claimVM,
                `lobs.commercialAuto.fixedPropertyIncident.propertyDescription.value`
            ),
            visible: !_.isUndefined(claimVM)
                    && !_.isUndefined(claimVM.lobs)
                    && !_.isUndefined(claimVM.lobs.commercialAuto)
                    && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident)
                    && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext)
                    && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value)
                    && !_.includes(['animal', 'personaleffect', 'electricvehiclechargingequipment'], claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value.code)
        },
        collisionlossTypeRadio:{
            visible: !_.isUndefined(claimVM)
            && !_.isUndefined(claimVM.lobs)
            && !_.isUndefined(claimVM.lobs.commercialAuto)
            && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident)
            && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext)
            && !_.isUndefined(claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value)
            && _.includes(['personaleffect', 'electricvehiclechargingequipment'], claimVM.lobs.commercialAuto.fixedPropertyIncident.propertyType_Ext.value.code),
            availableValues: lossPartyTypeList,
            value: _.get(
                claimVM,
                `lobs.commercialAuto.fixedPropertyIncident.lossType.value.code`
            )
        },
        lossLocationComponent: {
            path: !_.isUndefined(claimVM) ? claimVM : null,
            updatePath: updateWizardData
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onHandlePropertyDamageChange: onHandlePropertyDamageChange,
            onHandleCollisionTypeChange: onHandleCollisionTypeChange,
            onValidate: onValidate
        },
        resolveComponentMap: {
            losslocationcomponent: FNOLLossLocation
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrideProps);
        },
        [claimVM, overrideProps]
    );

    return (
        <WizardPage
            skipWhen={initialValidation}
            template={WizardPageTemplate}
            nextButtonID={translator(messages.fnolDetailPageNextButton)}
            disableNext={!isComponentValid}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
            />
        </WizardPage>
    );
}

FNOLCACollisionDetailsPage.propTypes = wizardProps;
export default withRouter(FNOLCACollisionDetailsPage);
